import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface DeliveryTerm {
    id: number;
    code: string;
    name: string;
    leadTimeInDays?: number;
    localizations: Localization[];
}

export async function getDeliveryTerm(id: number): Promise<DeliveryTerm> {
    const result = await rms2Fetch<DeliveryTerm>(`deliveryterms/${id}`);
    if (!result) throw new Error('Empty response for getDeliveryTerm');
    return result;
}

export async function createDeliveryTerm(dt: Partial<DeliveryTerm>): Promise<DeliveryTerm> {
    const result = await rms2Fetch<DeliveryTerm>('deliveryterms', {
        method: 'POST',
        body: JSON.stringify({
            code: dt.code,
            name: dt.name,
            leadTimeInDays: dt.leadTimeInDays ?? 0,
            localizations: dt.localizations?.map((l) => ({ languageId: l.language.id, name: l.name }))
        })
    });
    if (!result) throw new Error('Empty response for createDeliveryTerm');
    return result;
}

export async function updateDeliveryTerm(dt: DeliveryTerm): Promise<DeliveryTerm> {
    const result = await rms2Fetch<DeliveryTerm>(`deliveryterms/${dt.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            code: dt.code,
            name: dt.name,
            leadTimeInDays: dt.leadTimeInDays ?? 0,
            localizations: dt.localizations?.map((l) => ({ languageId: l.language.id, name: l.name }))
        })
    });
    if (!result) throw new Error('Empty response for updateDeliveryTerm');
    return result;
}

export async function deleteDeliveryTerm(id: number): Promise<void> {
    await rms2Fetch<void>(`deliveryterms/${id}`, { method: 'DELETE' });
}

export async function getLanguages(): Promise<Language[]> {
    const response = await rms2Fetch<{ languages: Language[] }>('languages');
    return response?.languages ?? [];
}
